/**
 * FmMegaMenu — Desktop mega-menu panel (7usro.6)
 * 
 * Structure: wide rounded panel below top nav, left groups + right promo card
 * FM owns layout: max-width, collision avoidance, semantic spacing props
 * Semantic props: groups, activeRouteId, onRouteClick, onClose
 * 
 * Desktop only (mega-menu at tablet/mobile is a drawer).
 * Controlled open state; keyboard handling (Escape to close, arrow keys to navigate).
 * Max-width prevents over-expansion; promo card optional.
 */

import React, { useEffect, useRef } from 'react';
import type { FmNavGroupEntry, FmRouteCatalogEntry } from './FmRouteCatalog';
import { FmNavGroup, FmNavPromoCard } from './FmNavComponents';
import { FmThemeIsland } from './FmThemeProvider';
import './FmMegaMenu.css';

export interface FmMegaMenuProps {
  /**
   * Groups to render in the menu.
   * Typically 2-4 groups, 3-6 routes per group.
   */
  groups: Array<{
    group: FmNavGroupEntry;
    routes: FmRouteCatalogEntry[];
  }>;

  /** Optional promo card (right rail). If present, groups sit on left. */
  promoCard?: {
    heading: string;
    body: string;
    actionLabel: string;
    actionHref?: string;
    onAction?: () => void;
    badge?: string;
    /** When true the promo target is coming-soon: CTA disabled + badged (c9tzf). */
    comingSoon?: boolean;
  };

  /** Currently active route (for highlighting). */
  activeRouteId?: string;

  /** Callback when a route is clicked. */
  onRouteClick?: (routeId: string, path: string) => void;

  /** Callback to close the menu (e.g. Escape key). */
  onClose?: () => void;

  /** Localized coming-soon badge label, forwarded to each group. */
  comingSoonLabel?: string;

  /** Test ID for E2E tests. */
  testId?: string;
}

/**
 * FmMegaMenu — desktop mega-menu panel.
 * Structure: left groups rail + optional right promo card.
 * Max-width, rounded corners, keyboard handling.
 */
export const FmMegaMenu: React.FC<FmMegaMenuProps> = ({
  groups,
  promoCard,
  activeRouteId,
  onRouteClick,
  onClose,
  comingSoonLabel,
  testId = 'fm-mega-menu',
}) => {
  const menuRef = useRef<HTMLDivElement>(null);
  // One group without a promo is a short list: the panel sizes to its content
  // and opens under its own trigger instead of spanning the full menu width.
  const compact = groups.length === 1 && !promoCard;

  // Keyboard handling: Escape to close
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose?.();
      }
    };

    const menu = menuRef.current;
    menu?.addEventListener('keydown', handleKeyDown);
    return () => menu?.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  // Focus trap: if user tabs out of menu, close it
  const handleFocusLeave = () => {
    setTimeout(() => {
      if (menuRef.current && !menuRef.current.contains(document.activeElement)) {
        onClose?.();
      }
    }, 0);
  };

  /* Light-theme island: the mega-menu is a DOM descendant of the transparent top
     bar, whose adaptive theme turns dark over a dark map band. This opaque dropdown
     always reads as the app-shell light surface (white panel, dark ink) to contrast
     the dark bar and map. */
  return (
    <FmThemeIsland theme="light">
    <div
      ref={menuRef}
      className={compact ? 'fm-mega-menu fm-mega-menu--compact' : 'fm-mega-menu'}
      data-testid={testId}
      role="menu"
      onBlur={handleFocusLeave}
    >
      <div
        className={
          promoCard
            ? 'fm-mega-menu__inner fm-mega-menu__inner--with-promo'
            : 'fm-mega-menu__inner'
        }
      >
        {/* Group columns */}
        <div className="fm-mega-menu__groups">
          {groups.map(({ group, routes }) => (
            <FmNavGroup
              key={group.id}
              heading={group.label}
              description={group.description}
              routes={routes}
              activeRouteId={activeRouteId}
              onRouteClick={onRouteClick}
              comingSoonLabel={comingSoonLabel}
            />
          ))}
        </div>

        {/* Right: optional promo card */}
        {promoCard && (
          <div className="fm-mega-menu__promo">
            <FmNavPromoCard
              heading={promoCard.heading}
              body={promoCard.body}
              actionLabel={promoCard.actionLabel}
              actionHref={promoCard.actionHref}
              onAction={promoCard.onAction}
              badge={promoCard.badge}
              comingSoon={promoCard.comingSoon}
              comingSoonLabel={comingSoonLabel}
            />
          </div>
        )}
      </div>
    </div>
    </FmThemeIsland>
  );
};

export default FmMegaMenu;
